import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ALLOWED_ATTACHMENT_MIME_TYPES,
  UnifiedAttachment,
} from '@this+that/models';
import { TFunction } from 'i18next';
import log from 'loglevel';

import { useCustomToast } from './useCustomToast';
import { MAX_FILE_SIZE_BYTES } from '../utils/constants';

export interface FileAttachment extends UnifiedAttachment {
  file: File;
}

export const formatAttachmentsForApi = (
  attachments: FileAttachment[],
): UnifiedAttachment[] =>
  attachments.map(({ name, contentType, contentBytes }) => ({
    name,
    contentType,
    contentBytes,
  }));

interface UseFileAttachmentsReturn {
  attachments: FileAttachment[];
  isLoading: boolean;
  addFiles: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  removeFile: (index: number) => void;
  clearAttachments: () => void;
  replaceAttachments: (next: FileAttachment[]) => void;
}

const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = (reader.result as string).split(',')[1];
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const validateFile = (
  t: TFunction,
  file: File,
): { isValid: boolean; error?: string } => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      isValid: false,
      error: t('attachments.errors.fileTooLarge', {
        fileName: file.name,
        maxSize: MAX_FILE_SIZE_BYTES / (1024 * 1024),
      }),
    };
  }

  if (!ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type)) {
    return {
      isValid: false,
      error: t('attachments.errors.fileTypeNotSupported', {
        fileName: file.name,
        fileType: file.type,
      }),
    };
  }
  return { isValid: true };
};

export const useFileAttachments = (
  initialAttachments?: FileAttachment[],
): UseFileAttachmentsReturn => {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();

  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const addFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setIsLoading(true);
    try {
      const newAttachments: FileAttachment[] = [];
      const fileArray = Array.from(files);

      for (const file of fileArray) {
        const validation = validateFile(t, file);
        if (!validation.isValid) {
          errorMessage(validation.error!);
          continue;
        }

        const isDuplicate = attachments.some(
          (attachment) =>
            attachment.name === file.name &&
            attachment.contentType === file.type,
        );

        if (isDuplicate) {
          errorMessage(
            t('attachments.errors.fileAlreadyAttached', {
              fileName: file.name,
            }),
          );
          continue;
        }

        try {
          const base64Content = await convertFileToBase64(file);

          const attachment: FileAttachment = {
            name: file.name,
            contentType: file.type,
            contentBytes: base64Content,
            file: file,
          };

          newAttachments.push(attachment);
        } catch (error) {
          log.error(`Error processing file ${file.name}:`, error);
          errorMessage(
            t('attachments.errors.failedToProcessFile', {
              fileName: file.name,
            }),
          );
        }
      }

      if (newAttachments.length > 0) {
        setAttachments((prev) => [...prev, ...newAttachments]);
      }
    } catch (error) {
      log.error('Error adding files:', error);
      errorMessage(t('attachments.errors.failedToAddFiles'));
    } finally {
      setIsLoading(false);
      event.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAttachments = () => {
    setAttachments([]);
  };

  const replaceAttachments = (next: FileAttachment[]) => {
    setAttachments(next);
  };

  useEffect(() => {
    if (initialAttachments && initialAttachments.length > 0) {
      setAttachments(initialAttachments);
    }
  }, [initialAttachments]);

  return {
    attachments,
    isLoading,
    addFiles,
    removeFile,
    clearAttachments,
    replaceAttachments,
  };
};
